
import { API_URL } from '../config/api';

// Utility function for API calls
const apiCall = async (endpoint, options = {}) => {
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  // Properly stringify the body if it's an object
  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  } else if (options.body) {
    config.body = options.body; // Already a string
  }

  console.log('Request config:', {
    url: `${API_URL}${endpoint}`,
    method: config.method,
    headers: config.headers,
    body: config.body
  });

  const response = await fetch(`${API_URL}${endpoint}`, config);
  
  if (!response.ok) {
    let errorMessage;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || `Request failed with status: ${response.status}`;
    } catch (e) {
      // If error response isn't JSON, get it as text
      const errorText = await response.text();
      errorMessage = `Request failed with status: ${response.status}. Response: ${errorText}`;
    }
    throw new Error(errorMessage);
  }
  
  return response.json();
};

// Authentication - PIN verification
// Replace the existing authenticatePin function with:
export const authenticatePin = async (pinCode, roomNo = null) => {
  try {
    const response = await fetch(`${API_URL}/api/manufacturing/verify-pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinCode, roomNo })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `PIN authentication failed with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('PIN authentication error:', error);
    throw error;
  }
};

// Get room inventory
// Replace the existing getRoomInventory function with:
export const getRoomInventory = async (roomNo, filters = {}) => {
  try {
    const response = await fetch(`${API_URL}/api/manufacturing/room/${roomNo}/inventory`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch room inventory with status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.inventory || [];
  } catch (error) {
    console.error(`Error fetching room inventory for room ${roomNo}:`, error);
    throw error;
  }
};



// Start work on task
export const startWorkTask = async (taskId, workerId, roomNo) => {
  try {
    const now = new Date();
    const localTime = now.getFullYear() + '-' + 
                     String(now.getMonth() + 1).padStart(2, '0') + '-' + 
                     String(now.getDate()).padStart(2, '0') + ' ' + 
                     String(now.getHours()).padStart(2, '0') + ':' + 
                     String(now.getMinutes()).padStart(2, '0') + ':' + 
                     String(now.getSeconds()).padStart(2, '0');
    
    const result = await apiCall(`/api/manufacturing/task/${taskId}/start`, {
      method: 'POST',
      body: { // Pass as object, apiCall will stringify it
        workerId, 
        roomNo,
        scanInTime: localTime
      },
    });
    
    return result;
  } catch (error) {
    console.error('Failed to start work:', error);
    throw error;
  }
};

export const finishWorkTask = async (taskId, workerId, roomNo) => {
  try {
    const now = new Date();
    const localTime = now.getFullYear() + '-' + 
                     String(now.getMonth() + 1).padStart(2, '0') + '-' + 
                     String(now.getDate()).padStart(2, '0') + ' ' + 
                     String(now.getHours()).padStart(2, '0') + ':' + 
                     String(now.getMinutes()).padStart(2, '0') + ':' + 
                     String(now.getSeconds()).padStart(2, '0');
    
    const result = await apiCall(`/api/manufacturing/task/${taskId}/finish`, { // ← Added /api/manufacturing
      method: 'POST',
      body: JSON.stringify({ // ← Added JSON.stringify
        workerId, 
        roomNo,
        scanOutTime: localTime
      }),
    });
    
    return result;
  } catch (error) {
    console.error('Failed to finish work:', error);
    throw error;
  }
};

export const pauseWorkTask = async (taskId, reason, workerId, roomNo) => {
  try {
    const now = new Date();
    const pauseTime = now.getFullYear() + '-' + 
                     String(now.getMonth() + 1).padStart(2, '0') + '-' + 
                     String(now.getDate()).padStart(2, '0') + ' ' + 
                     String(now.getHours()).padStart(2, '0') + ':' + 
                     String(now.getMinutes()).padStart(2, '0') + ':' + 
                     String(now.getSeconds()).padStart(2, '0');
    
    const result = await apiCall(`/api/manufacturing/task/${taskId}/pause`, { // ← Added /api/manufacturing
      method: 'POST',
      body: JSON.stringify({ // ← Added JSON.stringify
        workerId, 
        reason, 
        roomNo,
        pauseTime: pauseTime
      }),
    });
    
    return result;
  } catch (error) {
    console.error('Failed to pause task:', error);
    throw error;
  }
};

export const resumeWorkTask = async (taskId, workerId, roomNo) => {
  try {
    const now = new Date();
    const resumeTime = now.getFullYear() + '-' + 
                      String(now.getMonth() + 1).padStart(2, '0') + '-' + 
                      String(now.getDate()).padStart(2, '0') + ' ' + 
                      String(now.getHours()).padStart(2, '0') + ':' + 
                      String(now.getMinutes()).padStart(2, '0') + ':' + 
                      String(now.getSeconds()).padStart(2, '0');
    
    const result = await apiCall(`/api/manufacturing/task/${taskId}/resume`, { // ← Added /api/manufacturing
      method: 'POST',
      body: JSON.stringify({ // ← Added JSON.stringify
        workerId, 
        roomNo,
        resumeTime: resumeTime
      }),
    });
    
    return result;
  } catch (error) {
    console.error('Failed to resume task:', error);
    throw error;
  }
};


// Get available workers for room
export const getAvailableWorkers = async (roomNo) => {
  try {
    const response = await fetch(`${API_URL}/api/manufacturing/room/${roomNo}/workers`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get workers with status: ${response.status}`);
    }
    
    const data = await response.json(); // ✅ Parse the JSON first
    return data.workers || [];
  } catch (error) {
    console.error('Failed to get workers:', error);
    throw error;
  }
};
// Error handling utility
export const handleManufacturingError = (error, defaultMessage = 'Operation failed') => {
  if (error?.message) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return defaultMessage;
};